import {
  Component, Input, Output, EventEmitter, OnInit, OnDestroy,
  ChangeDetectionStrategy, ChangeDetectorRef
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDividerModule } from '@angular/material/divider';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { CustomFlexLayoutModule } from 'src/app/shared/flex-layout/custom-flex-layout.module';
import { LeadDelegationService } from 'src/app/services/lead-delegation.service';

/**
 * DelegationPanelComponent
 *
 * Handles lead/deal ownership transfer UI.
 *
 * Usage:
 *   <calsoft-delegation-panel
 *     resourceType="LEAD"
 *     [resourceId]="lead.entityId"
 *     [currentOwnerId]="lead.ownerId"
 *     [currentOwnerName]="lead.ownerName"
 *     [currentUser]="currentUser"
 *     [userList]="users"
 *     (delegated)="onDelegated($event)">
 *   </calsoft-delegation-panel>
 */
@Component({
  selector: 'calsoft-delegation-panel',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatExpansionModule,
    MatDividerModule,
    CustomFlexLayoutModule
  ],
  templateUrl: './delegation-panel.component.html',
  styleUrls: ['./delegation-panel.component.scss']
})
export class DelegationPanelComponent implements OnInit, OnDestroy {

  @Input() resourceType = 'LEAD';
  @Input() resourceId!: number;
  @Input() currentOwnerId!: number;
  @Input() currentOwnerName = '';
  @Input() currentUser: any = {};
  @Input() userList: any[] = [];

  @Output() delegated = new EventEmitter<any>();

  history: any[] = [];
  saving = false;
  showForm = false;

  form!: FormGroup;

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private delegationService: LeadDelegationService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      newOwnerId:    [null, Validators.required],
      newOwnerName:  [''],
      newOwnerEmail: [''],
      reason:        ['', Validators.maxLength(500)]
    });
    this.loadHistory();
  }

  loadHistory(): void {
    this.delegationService
      .getHistory(this.resourceType, this.resourceId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((h: any[]) => {
        this.history = h;
        this.cdr.markForCheck();
      });
  }

  onOwnerChange(userId: any): void {
    const user = this.userList.find(u => (u.id ?? u.userId) === userId);
    if (user) {
      this.form.patchValue({
        newOwnerName: user.name ?? user.userName,
        newOwnerEmail: user.email ?? ''
      });
    }
  }

  submit(): void {
    if (this.form.invalid || this.saving) return;
    this.saving = true;

    const payload = {
      resourceType: this.resourceType,
      resourceId: this.resourceId,
      previousOwnerId: this.currentOwnerId,
      previousOwnerName: this.currentOwnerName,
      newOwnerId: this.form.value.newOwnerId,
      newOwnerName: this.form.value.newOwnerName,
      newOwnerEmail: this.form.value.newOwnerEmail,
      delegatedBy: this.currentUser?.id,
      delegatedByName: this.currentUser?.name ?? 'System',
      reason: this.form.value.reason
    };

    this.delegationService.delegate(payload)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (res) => {
          this.saving = false;
          this.showForm = false;
          this.form.reset();
          this.loadHistory();
          this.delegated.emit(res);
          this.cdr.markForCheck();
        },
        error: () => { this.saving = false; this.cdr.markForCheck(); }
      });
  }

  trackById(_: number, item: any): any { return item.id; }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
